import { useState, useMemo, type CSSProperties, type ReactNode } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Heart, Footprints, BedDouble, Activity } from 'lucide-react'
import { Button } from '@/components/ui'
import { LoadingSpinner } from '@/components/shared'
import {
  StepsChart,
  HeartRateChart,
  SleepChart,
  PeriodSelector,
  WellbeingHero,
} from '@/components/health'
import {
  SectionHeader,
  QuickStatCard,
  ConnectionStatusChip,
  avatarTint,
  initialsOf,
  type StatTrend,
} from '@/components/hearth'
import { cn } from '@/lib/utils'
import {
  useFamily,
  useFamilyMembers,
  useHealthConnections,
  useMemberHealthMetrics,
  useHealthSummary,
  useHealthTrends,
  getPeriodDateRange,
  groupMetricsByDate,
  type HealthPeriod,
} from '@/hooks'
import type { MetricType, TrendDirection } from '@/types'

/** Staggered Hearth entrance wrapper */
function Appear({ order, children, className }: { order: number; children: ReactNode; className?: string }) {
  return (
    <div className={cn('hearth-appear', className)} style={{ '--stagger': order } as CSSProperties}>
      {children}
    </div>
  )
}

function formatSleepHours(hours: number) {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

export function HealthDetailPage() {
  const { memberId } = useParams<{ memberId: string }>()
  const [period, setPeriod] = useState<HealthPeriod>('7d')
  const { selectedFamilyId } = useFamily()
  const { members } = useFamilyMembers(selectedFamilyId)
  const { connections } = useHealthConnections(selectedFamilyId)

  const member = members.find((m) => m.id === memberId)
  const memberConnections = connections.filter(
    (connection) => connection.parent_user === member?.user.id
  )
  const hasConnection = memberConnections.some((c) => c.status === 'CONNECTED')

  // Get date range for the selected period
  const dateRange = getPeriodDateRange(period)

  // Fetch health summary for metric cards
  // Note: Backend requires parent_user_id (user ID), not member_id
  const { data: summary, isLoading: isSummaryLoading } = useHealthSummary(selectedFamilyId, {
    parent_user_id: member?.user.id,
    period,
  })

  // Fetch health trends for trend indicators
  const { data: trends, isLoading: isTrendsLoading } = useHealthTrends(selectedFamilyId, {
    parent_user_id: member?.user.id,
    period,
  })

  // Fetch raw metrics for charts - get steps
  const { data: stepsData, isLoading: isStepsLoading } = useMemberHealthMetrics(
    selectedFamilyId,
    member?.user.id ?? null,
    {
      metric_type: 'STEPS',
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
      page_size: 500, // Get enough data points
    }
  )

  // Fetch heart rate metrics
  const { data: heartRateData, isLoading: isHeartRateLoading } = useMemberHealthMetrics(
    selectedFamilyId,
    member?.user.id ?? null,
    {
      metric_type: 'HEART_RATE',
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
      page_size: 500,
    }
  )

  // Fetch sleep duration metrics
  const { data: sleepData, isLoading: isSleepLoading } = useMemberHealthMetrics(
    selectedFamilyId,
    member?.user.id ?? null,
    {
      metric_type: 'SLEEP_DURATION',
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
      page_size: 500,
    }
  )

  // Transform metrics data for charts
  const stepsChartData = useMemo(() => {
    if (!stepsData?.data?.length) return []
    return groupMetricsByDate(stepsData.data.map((m) => ({ timestamp: m.timestamp, value: m.value })))
      .map((d) => ({ date: d.date, value: Math.round(d.value) }))
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [stepsData])

  const heartRateChartData = useMemo(() => {
    if (!heartRateData?.data?.length) return []
    return groupMetricsByDate(heartRateData.data.map((m) => ({ timestamp: m.timestamp, value: m.value })))
      .map((d) => ({
        date: d.date,
        value: Math.round(d.value),
        min: Math.round(d.min),
        max: Math.round(d.max),
      }))
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [heartRateData])

  const sleepChartData = useMemo(() => {
    if (!sleepData?.data?.length) return []
    return groupMetricsByDate(sleepData.data.map((m) => ({ timestamp: m.timestamp, value: m.value })))
      .map((d) => ({
        date: d.date,
        value: parseFloat(d.value.toFixed(1)),
        quality: 80, // Default quality if not available
      }))
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [sleepData])

  // Helper to get the Hearth stat trend for a metric type
  const statTrend = (metricType: MetricType): StatTrend | undefined => {
    const trend = trends?.metrics?.[metricType]
    if (!trend) return undefined
    const direction =
      trend.trend === 'improving' ? 'up' : trend.trend === 'declining' ? 'down' : 'stable'
    const changePercent = trend.change_percent ?? 0
    return {
      direction,
      label: changePercent ? `${Math.abs(changePercent).toFixed(0)}%` : undefined,
    }
  }

  // Helper for the chart-header trend pill
  const chartTrend = (
    metricType: MetricType
  ): { direction: TrendDirection; changePercent: number } | undefined => {
    const trend = trends?.metrics?.[metricType]
    if (!trend) return undefined
    return { direction: trend.trend, changePercent: trend.change_percent }
  }

  // Helper to format metric value
  const formatMetricValue = (metricType: MetricType): string => {
    const metric = summary?.metrics_by_type?.[metricType]
    if (!metric) return '--'

    switch (metricType) {
      case 'STEPS':
        return metric.average.toLocaleString(undefined, { maximumFractionDigits: 0 })
      case 'HEART_RATE':
      case 'HEART_RATE_RESTING':
        return `${metric.average.toFixed(0)} bpm`
      case 'SLEEP_DURATION':
        return formatSleepHours(metric.average)
      default:
        return metric.average.toFixed(1)
    }
  }

  // Get resting heart rate for chart
  const restingHeartRate = summary?.metrics_by_type?.HEART_RATE_RESTING?.average

  const isLoading = isSummaryLoading || isTrendsLoading
  const isChartsLoading = isStepsLoading || isHeartRateLoading || isSleepLoading
  const hasData = (summary?.total_data_points ?? 0) > 0

  if (!member) {
    return (
      <div className="hearth-card-floating flex flex-col items-center justify-center px-6 py-16 text-center">
        <h2 className="font-serif text-xl font-semibold text-gray-900">
          We couldn't find that family member
        </h2>
        <p className="mt-2 text-[15px] text-gray-500">
          They may have been removed, or the link may be out of date.
        </p>
        <Button asChild variant="secondary" className="mt-5">
          <Link to="/health">Back to Health</Link>
        </Button>
      </div>
    )
  }

  const fullName = member.user.full_name
  const firstName = fullName.split(' ')[0]
  const tint = avatarTint(fullName)
  const primaryConnection = memberConnections[0]

  let stagger = 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <Appear order={stagger++}>
        <Link
          to="/health"
          className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Health
        </Link>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div
              className={cn(
                'flex h-14 w-14 items-center justify-center rounded-full text-lg font-semibold',
                tint.bg,
                tint.fg
              )}
            >
              {initialsOf(fullName)}
            </div>
            <div>
              <h1 className="font-serif text-2xl font-semibold text-gray-900">{fullName}</h1>
              <p className="mt-0.5 text-sm capitalize text-gray-500">
                {member.role.replace('_', ' ').toLowerCase()}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ConnectionStatusChip
              title={
                primaryConnection
                  ? primaryConnection.source === 'APPLE_HEALTH'
                    ? 'Apple Health'
                    : 'Google Health Connect'
                  : 'Health sharing'
              }
              state={hasConnection ? 'connected' : 'disconnected'}
              lastUpdated={primaryConnection?.last_sync_at}
            />
            <PeriodSelector value={period} onChange={setPeriod} />
          </div>
        </div>
      </Appear>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* No connection state */}
      {!isLoading && !hasConnection && !hasData && (
        <Appear order={stagger++}>
          <div className="hearth-card-floating flex flex-col items-center px-6 py-10 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
              <Heart className="h-8 w-8 text-primary-600" />
            </div>
            <h2 className="mt-4 font-serif text-xl font-semibold text-gray-900">
              Nothing to see just yet
            </h2>
            <p className="mt-2 max-w-md text-[15px] text-gray-500">
              Once {firstName} turns on health sharing from the FamSteady app on their phone,
              readings will gently appear here as they sync.
            </p>
          </div>
        </Appear>
      )}

      {/* Data display */}
      {!isLoading && (hasConnection || hasData) && (
        <>
          {/* Wellbeing hero */}
          <Appear order={stagger++}>
            <WellbeingHero
              name={firstName}
              score={summary?.health_score}
              insight={summary?.insights?.[0]}
            />
          </Appear>

          {/* Metric grid */}
          <Appear order={stagger++}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <QuickStatCard
                icon={Footprints}
                iconClassName="bg-success-500/10 text-success-500"
                title="Steps"
                value={formatMetricValue('STEPS')}
                subtitle="Daily average"
                trend={statTrend('STEPS')}
                sparklineData={stepsChartData.map((d) => d.value)}
                sparklineColorClass="text-success-500"
              />
              <QuickStatCard
                icon={Heart}
                iconClassName="bg-accent-500/10 text-accent-500"
                title="Heart rate"
                value={formatMetricValue('HEART_RATE')}
                subtitle="Average"
                trend={statTrend('HEART_RATE')}
                sparklineData={heartRateChartData.map((d) => d.value)}
                sparklineColorClass="text-accent-500"
              />
              <QuickStatCard
                icon={Activity}
                iconClassName="bg-info-500/10 text-info-500"
                title="Resting heart rate"
                value={formatMetricValue('HEART_RATE_RESTING')}
                subtitle="At rest"
                trend={statTrend('HEART_RATE_RESTING')}
              />
              <QuickStatCard
                icon={BedDouble}
                iconClassName="bg-[#7c6fa8]/10 text-[#7c6fa8]"
                title="Sleep"
                value={formatMetricValue('SLEEP_DURATION')}
                subtitle="Nightly average"
                trend={statTrend('SLEEP_DURATION')}
                sparklineData={sleepChartData.map((d) => d.value)}
                sparklineColorClass="text-[#7c6fa8]"
              />
            </div>
          </Appear>

          {/* Charts */}
          <Appear order={stagger++}>
            <div className="space-y-3">
              <SectionHeader title="Recent readings" />
              {isChartsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <LoadingSpinner size="lg" />
                </div>
              ) : stepsChartData.length === 0 &&
                heartRateChartData.length === 0 &&
                sleepChartData.length === 0 ? (
                <div className="hearth-card p-6 text-center">
                  <p className="text-sm text-gray-500">
                    No readings for this period yet. They'll appear here as {firstName}'s phone
                    syncs.
                  </p>
                </div>
              ) : (
                <>
                  <StepsChart data={stepsChartData} goal={10000} trend={chartTrend('STEPS')} />
                  <div className="grid gap-4 lg:grid-cols-2">
                    <HeartRateChart
                      data={heartRateChartData}
                      restingHeartRate={restingHeartRate ? Math.round(restingHeartRate) : undefined}
                      trend={chartTrend('HEART_RATE')}
                    />
                    <SleepChart
                      data={sleepChartData}
                      goalHours={8}
                      trend={chartTrend('SLEEP_DURATION')}
                    />
                  </div>
                </>
              )}
            </div>
          </Appear>
        </>
      )}
    </div>
  )
}
